import { useEffect, useCallback, useState } from 'react';
import { insertAuditTrail } from '../../../../../API/GoMoto';
import { Events } from '../../../../../API/Enums';
import { useAppointmentState, useAppointmentUpdateAndPersist, useLockerOperation, useTowInProcess } from '../../../../../context/Appointment/appointment';
import { useFullReset, useKioskImage, useKioskState } from '../../../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../../../context/Localization/localization';
import { useHideKioskBackground } from '../../../../../context/Page/page';
import { CTAButton } from '../../../../../controls/ButtonBase';
import { Heading } from '../../../../../controls/Heading/Heading';
import { KioskImageDisplay } from '../../../../../controls/KioskImageDisplay';
import { ScrollableBody } from '../../../../Layout/ScrollableBody';
import { Spinner } from '../../../../../controls/Spinner/Spinner';
import { GMDialogError, GMDialogRetryCancel } from '../../../../../controls/Dialog';
import { GMButtonGroup } from '../../../../../controls/ButtonGroup';
import { GMRoundedIcon, GMSizedIcon } from '../../../../../controls/Icon';
import { KeyDropError } from '../../../../../context/Appointment/appointment.types';
import { GoMotoComponent } from '../../../../../API/GoMoto.types';
import { KeyDropProps } from './KeyDrop.types';
import { LocalizationType } from '../../../../../context/Localization/localization.types';

/**
 * Handles the store key logic for the Express, Cube, and 24/7 Kiosk types.
 *
 * @param onProceed - {@link KeyDropProps.onProceed onProceed} description.
 * @param onSkip - {@link KeyDropProps.onSkip onSkip} description.
 * @param doubleChecking - {@link KeyDropProps.doubleChecking doubleChecking} description.
 */
export const KeyDrop: GoMotoComponent<KeyDropProps> = ({
    onProceed,
    onSkip,
    doubleChecking,
    sessionLockerId,
    onLockerAssigned,
}) => {
    const Localization: LocalizationType = useLocalization();
    const hideKioskBackground = useHideKioskBackground();
    const { kioskType, keyStorageType } = useKioskState();
    const reset = useFullReset();
    const kioskLeftImage = useKioskImage('KeyDrop');
    const kioskRightImage = useKioskImage('KeyDropWarning');
    const { start, locker, inProgress, complete, hasOpened, error: keyDropError, forceLockerClose } = useLockerOperation();
    const appointment = useAppointmentState();
    const towInProcess = useTowInProcess();
    const setAppointment = useAppointmentUpdateAndPersist();

    const [error, setError] = useState<string | undefined>(undefined);
    const [retryError, setRetryError] = useState<string | undefined>(undefined);

    useEffect(() => {
        hideKioskBackground();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (locker && !doubleChecking) {
            onLockerAssigned?.(locker.Id);
        }
    }, [locker, doubleChecking, onLockerAssigned]);

    const lockerClosed: VoidFunction = useCallback(async () => {
        if (kioskType !== 'express') {
            await insertAuditTrail(appointment.key, Events.Customer_Dropped_Off);

            if (towInProcess) {
                await setAppointment({
                    ...appointment,
                    status: 'scheduled',
                });
            }
        }
        if (!complete) {
            await forceLockerClose();
        }
        onProceed();
    }, [appointment, kioskType, onProceed, setAppointment, complete, towInProcess, forceLockerClose]);


    const onRetryOpenLocker: VoidFunction = () => {
        setRetryError(undefined);
        start({
            lockerAction: 'store-key',
            resetAttempts: true,
        });
    };

    useEffect(() => {
        if (kioskType === 'express' && !complete && !inProgress && !error && keyDropError === KeyDropError.none) {
            if (doubleChecking) {
                if (sessionLockerId !== undefined) {
                    insertAuditTrail(appointment.key, Events.ReopenedLocker, undefined, sessionLockerId.toString());
                    start({
                        lockerAction: 'open-locker',
                        lockerId: sessionLockerId,
                    });
                }
                else {
                    start({ lockerAction: 'store-key' });
                }
            }
            else {
                start({
                    lockerAction: 'store-key',
                });
            }
        }
        if (complete) {
            lockerClosed();
        }
    }, [appointment.key, sessionLockerId, complete, error, inProgress, keyDropError, kioskType, start, lockerClosed, doubleChecking]);

    useEffect(() => {
        if (keyDropError !== KeyDropError.none) {
            switch (keyDropError) {
                case KeyDropError.noLockerAvailable:
                    setError(Localization.NoLockerAvailableSeeAdvisor);
                    break;
                case KeyDropError.keyAlreadyStored:
                    setError(Localization.KeyAlreadyAssigned);
                    break;
                case KeyDropError.failedToOpen:
                    setRetryError(Localization.FailedToOpenLocker);
                    break;
                default:
                    setError(Localization.AnUnknownErrorHasOccurred);
                    break;
            }
        }
    }, [
        error,
        setError,
        Localization.AnUnknownErrorHasOccurred,
        Localization.KeyAlreadyAssigned,
        Localization.NoLockerAvailableSeeAdvisor,
        Localization.FailedToOpenLocker,
        keyDropError,
    ]);

    useEffect(() => {
        if (!keyStorageType) {
            onSkip();
        }
    }, [keyStorageType, onSkip]);

    if (!keyStorageType) {
        return (
            <Spinner />
        );
    }
    else {
        return (
            <ScrollableBody
                heading={
                    <Heading
                        roundedIcon={
                            <GMRoundedIcon
                                roundedBackgroundSize={'large'}
                                themeType={'secondary'}
                            >
                                <GMSizedIcon
                                    iconType={kioskType === 'express' ? 'Key' : 'Complete'}
                                    iconSize={kioskType === 'express' ? 'large' : 'extraLarge'}
                                />
                            </GMRoundedIcon>
                        }
                        primaryHeading={kioskType === 'express' ? Localization.PlaceKeyNow : Localization.DropKeyNow}
                        secondaryHeading={
                            kioskType === 'express' && locker
                                ? Localization.formatString(
                                    towInProcess ? Localization.YourAssignedLockerDropOff : Localization.YourAssignedLocker,
                                    <b>{Localization.formatString(Localization.LockerNum, locker.Id)}</b>,
                                )
                                : (towInProcess ? Localization.PleaseDropTheKey : Localization.PleaseDropYourKey)
                        }
                        hint={kioskType !== 'express' ? Localization.WhenFinishedTapButton : undefined}
                        showMustache
                    />
                }
                proceedButtons={
                    <GMButtonGroup
                        buttons={[
                            <CTAButton
                                styleType={'primary'}
                                onClick={lockerClosed}
                                label={kioskType === 'express' ? Localization.LockerClosed : Localization.KeyDroppedOff}
                                kioskOptions={{
                                    inlineOptions: {
                                        sizedIcon: (
                                            <GMSizedIcon
                                                iconSize={'custom'}
                                                iconType={'Complete'}
                                                ariaLabel={Localization.CompleteCheckmarkIcon}
                                            />
                                        ),
                                    },
                                }}
                                id={kioskType === 'express' ? Localization.LockerClosed : Localization.KeyDroppedOff}
                                ariaLabel={kioskType === 'express' ? Localization.LockerClosed : Localization.KeyDroppedOff}
                                disabled={kioskType === 'express' && !hasOpened}
                            />,
                        ]}
                    />
                }
            >
                <KioskImageDisplay
                    left={{
                        image: kioskLeftImage,
                    }}
                    right={{
                        top: {
                            text: kioskType === 'express' ? (
                                towInProcess ? Localization.PlaceTheKeyIntoOpenLocker : Localization.PlaceYourKeyIntoOpenLocker
                            ) : (
                                towInProcess ? Localization.DropTheKeyInTheDoor : Localization.DropYourKeyInTheDoor
                            ),
                        },
                        image: kioskRightImage,
                        bottom: kioskType === 'express' ? (
                            towInProcess ? Localization.AfterClosingLockerDoorDropOff : Localization.AfterClosingLockerDoor
                        ) : (
                            towInProcess ? Localization.AfterDroppingYourKeyDropOff : Localization.AfterDroppingYourKey
                        ),
                        reminder: towInProcess ? '' : Localization.ReminderDropKey,
                    }}
                />
                <GMDialogError
                    ariaLabel={Localization.AnErrorHasOccured}
                    open={!!error}
                    onClose={() => {
                        setError(undefined);
                        reset();
                    }}
                >
                    {error}
                </GMDialogError>
                <GMDialogRetryCancel
                    ariaLabel={Localization.Retry}
                    open={!!retryError}
                    onCancel={() => {
                        setRetryError(undefined);
                        reset();
                    }}
                    onRetry={onRetryOpenLocker}
                >
                    {retryError}
                </GMDialogRetryCancel>
            </ScrollableBody>
        );
    }
};
